import { ArrowRightOutlined } from '@ant-design/icons'
import { BBDTag, GraviButton, GraviGrid, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { ColDef, GetRowIdParams, ICellRendererParams, IFilter, RowHeightParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import * as React from 'react'

import { useLaunchpadContext } from '../context'
import type {
  InvoiceDetail,
  InvoiceTransaction,
  TransactionDetail,
  TransactionRow,
} from '../../../../api/useFreight/types'
import { orderColumnDefs } from '../../FreightLineItems/components/columnDefs/byOrderColumnDefs'
import { byOrderLineItemColumns } from '../../FreightLineItems/components/columnDefs'

export function AttentionItems() {
  const { data, triggerDrawer, costRevenue } = useLaunchpadContext()

  if (!data) {
    return null
  }

  const { attentionItems } = data

  const getRowHeight = (data: InvoiceDetail | undefined) => {
    if (!data) {
      return 30
    }
    return data.total_override || data.amount_override ? 40 : 30
  }
  const errorColumnDefs: ColDef<TransactionRow | InvoiceTransaction>[] = [
    {
      headerName: '',
      field: 'order_id',
      width: 50,
      flex: 0,
      maxWidth: 50,
      cellRenderer: (params: ICellRendererParams) => {
        const triggerDrawerWithPayload = () => {
          const currentIndex = attentionItems.noRateFound.findIndex((order) => order.order_id === params.data.order_id)
          const orders = attentionItems.noRateFound
          triggerDrawer({
            visible: true,
            orderNumber: params.data.order_number,
            currentIndex,
            orders,
          })
        }
        return (
          <GraviButton
            onClick={triggerDrawerWithPayload}
            icon={<MaterialIcon className='pt-1' icon='top_panel_close' />}
            size='small'
          >
            {params.data.reference}
          </GraviButton>
        )
      },
    },
    {
      headerName: 'ORDER',
      field: 'order_number',
      flex: 1,
    },
    ...orderColumnDefs.filter(
      (col: ColDef<TransactionRow | InvoiceTransaction>) =>
        ![
          'order_number',
          'date',
          'status',
          'carrier_name',
          'destinations',
          'distance',
          'volume',
          'net_volume',
          'gross_volume',
          'base_total',
          'accessorial_total',
          'surcharge_total',
          'total',
          'id',
        ].includes(col.field as string)
    ),
  ]

  return (
    <Vertical bg='default' border='all' borderRadius='6px'>
      <Horizontal flex='0' style={{ overflow: 'visible' }} border='bottom' verticalCenter className='p-3 '>
        <Texto category='p2'>Items Needing Attention</Texto>
      </Horizontal>

      {/* No Rate Found Section */}
      {attentionItems.noRateFound.length > 0 && (
        <Vertical flex='0' style={{ overflow: 'visible' }} gap={8}>
          <Horizontal className='px-3 py-2' border='bottom' gap={8} verticalCenter justifyContent='space-between'>
            <BBDTag color='error'>● No Rate Found</BBDTag>
            <Horizontal gap={8} verticalCenter>
              <Texto category='p2' weight='bold'>
                {attentionItems.noRateFound.length}
              </Texto>
              <GraviButton
                onClick={() => {
                  window.open(`freight_line_items?status=error&view=flat&costRevenue=${costRevenue}`, '_blank')
                }}
                appearance='link'
                size='small'
                icon={<ArrowRightOutlined />}
              >
                View All
              </GraviButton>
            </Horizontal>
          </Horizontal>
          <div
            style={{
              maxHeight: attentionItems.accessorialApprovals.length === 0 ? '45vh' : '100%',
              height: attentionItems.noRateFound.length * 30 + 50,
            }}
          >
            <GraviGrid<TransactionRow | InvoiceTransaction, IFilter>
              rowData={attentionItems.noRateFound}
              sideBar={undefined}
              storageKey='no-rate-found'
              columnDefs={errorColumnDefs}
              agPropOverrides={{
                headerHeight: 32,
                scrollbarWidth: 0,
                rowHeight: 30,
                pagination: false,
                suppressPaginationPanel: true,
                rowGroupPanelShow: 'onlyWhenGrouping',
                getRowId: (params: GetRowIdParams) => params.data.order_id,
                animateRows: true,
              }}
            />
          </div>
        </Vertical>
      )}

      {/* Accessorial Approval Section */}
      <Vertical gap={8}>
        <Horizontal
          style={{ overflow: 'visible' }}
          border='bottom'
          className='px-3 py-2'
          gap={8}
          verticalCenter
          justifyContent='space-between'
        >
          <BBDTag color='warning'>● Needs Approval</BBDTag>
          <Horizontal gap={8} verticalCenter>
            <Texto category='p2' weight='bold'>
              {attentionItems.accessorialApprovals.length}
            </Texto>
            <GraviButton
              onClick={() => {
                window.open(`freight_line_items?status=needs_approval&view=flat&costRevenue=${costRevenue}`, '_blank')
              }}
              appearance='link'
              size='small'
              icon={<ArrowRightOutlined />}
            >
              View All
            </GraviButton>
          </Horizontal>
        </Horizontal>

        <div
          style={{
            minHeight: attentionItems.noRateFound.length === 0 ? '45vh' : '100%',
            height: '100%',
          }}
        >
          <GraviGrid<InvoiceDetail, IFilter>
            primaryKey='id'
            rowData={attentionItems.accessorialApprovals}
            sideBar={undefined}
            storageKey='needs-attention-accessorial'
            columnDefs={[
              {
                headerName: '',
                field: 'id',
                width: 50,
                flex: 0,
                maxWidth: 50,
                cellRenderer: (params: ICellRendererParams) => {
                  const triggerDrawerWithPayload = () => {
                    const currentIndex = attentionItems.needsApprovalOrders.findIndex((order) =>
                      order.details.some((detail) => detail.id === params.data.id)
                    )
                    const orders = attentionItems.needsApprovalOrders
                    triggerDrawer({
                      visible: true,
                      orderNumber: params.data.order_number,
                      currentIndex,
                      orders,
                    })
                  }

                  return (
                    <GraviButton
                      icon={<MaterialIcon className='pt-1' icon='top_panel_close' />}
                      size='small'
                      onClick={triggerDrawerWithPayload}
                    >
                      {params.data.reference}
                    </GraviButton>
                  )
                },
              },
              // @ts-ignore: ExtendedColDef is compatible but type definition is too strict
              ...byOrderLineItemColumns().filter(
                (col) =>
                  !['counterparty', 'product_group', 'status', 'type', 'book_type', 'id'].includes(col.field as string)
              ),
            ]}
            agPropOverrides={{
              context: {
                triggerDrawer,
              },
              headerHeight: 32,
              scrollbarWidth: 0,
              pagination: false,
              suppressPaginationPanel: true,
              rowGroupPanelShow: 'onlyWhenGrouping',
              getRowHeight: (params: RowHeightParams<InvoiceDetail>) => getRowHeight(params.data),
              getRowId: (params) => params.data.id,

              animateRows: true,
            }}
          />
        </div>
      </Vertical>
    </Vertical>
  )
}
